/**
 * ModFlode — Helper-Flöde: Review-first helperflöde. En kronologisk ström över befintliga evenemang och kanoniska signaler; signaler publiceras inte automatiskt.
 */
import { useEffect, useState, useCallback } from "react";
import { Link, Navigate } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft, RefreshCw, Loader2, Calendar, MapPin,
  Zap, PenLine, XCircle, Bell, ExternalLink, Eye, Clock,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const modHeaders = () => ({ Authorization: `Bearer ${sessionStorage.getItem("mod_token")}` });

function relTime(ts) {
  if (!ts) return "";
  const d = new Date(ts);
  if (isNaN(d.getTime())) return ts;
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 90) return "just nu";
  if (diff < 3600) return `för ${Math.round(diff / 60)} min sedan`;
  if (diff < 86400) return `för ${Math.round(diff / 3600)} tim sedan`;
  if (diff < 172800) return "igår";
  return d.toLocaleDateString("sv-SE");
}

const BADGES = {
  auto: { label: "Automatiskt", cls: "bg-pine/15 text-pine border-pine/40", icon: Zap },
  manuell: { label: "Manuellt", cls: "bg-bone-alt text-bark border-warm-border", icon: PenLine },
  avbokat: { label: "Avbokat", cls: "bg-falu/10 text-falu border-falu/40", icon: XCircle },
  signal: { label: "Internt signal", cls: "bg-amber-100 text-amber-900 border-amber-300", icon: Bell },
};

const SIGNAL_KIND_LABELS = {
  announcement: "Evenemangsannons",
  update: "Ändring",
  cancellation: "Avbokning",
  result: "Resultat",
  after_report: "Efterrapport",
  media: "Media",
  other: "Övrigt",
};

function Avatar({ name }) {
  const initial = (name || "V").trim().charAt(0).toUpperCase();
  return (
    <span className="w-9 h-9 shrink-0 rounded-full bg-falu/10 border border-falu/30 text-falu font-heading font-bold text-sm flex items-center justify-center">
      {initial}
    </span>
  );
}

export default function ModFlode() {
  const token = sessionStorage.getItem("mod_token");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("alla");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/mod/flode`, { headers: modHeaders() });
      setData(res.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!token) return <Navigate to="/mod/login" replace />;

  const items = (data?.items || []).filter((i) => {
    if (filter === "alla") return true;
    if (filter === "evenemang") return i.type === "evenemang";
    if (filter === "signaler") return i.type === "signal";
    if (filter === "granskning") return i.type === "signal" && !i.reviewed;
    return true;
  });
  const c = data?.counts || {};

  const FILTERS = [
    { key: "alla", label: `Alla` },
    { key: "evenemang", label: `Evenemang (${c.evenemang ?? 0})` },
    { key: "signaler", label: `Signaler (${c.signaler ?? 0})` },
    { key: "granskning", label: `Behöver granskning (${c.behover_granskning ?? 0})` },
  ];

  return (
    <div data-testid="mod-flode-page" className="max-w-2xl mx-auto px-4 sm:px-6 py-8 sm:py-10">
      <Link to="/mod" className="inline-flex items-center gap-1.5 text-sm text-pine hover:text-pine-hover no-underline mb-4">
        <ArrowLeft size={14} /> Till panelen
      </Link>

      <div className="flex items-center justify-between gap-3 mb-1">
        <h1 className="font-fraktur-smooth text-2xl sm:text-3xl text-falu tracking-wide">Flödet</h1>
        <button
          type="button"
          onClick={load}
          data-testid="flode-refresh-btn"
          className="inline-flex items-center gap-1.5 text-xs font-semibold border border-pine text-pine hover:bg-bone-alt px-3 py-1.5 rounded-sm transition-colors"
        >
          <RefreshCw size={13} /> Uppdatera
        </button>
      </div>
      <p className="text-sm text-bark mb-5 leading-relaxed">
        Överblick över evenemang och inkomna signaler, nyast först. Signaler med granskningsbehov publiceras inte automatiskt.
      </p>

      <div className="flex flex-wrap gap-2 mb-6" data-testid="flode-filter-bar">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            onClick={() => setFilter(f.key)}
            data-testid={`flode-filter-${f.key}`}
            className={`text-xs font-semibold px-3.5 py-1.5 rounded-full border transition-colors ${
              filter === f.key
                ? "bg-ink text-parchment border-ink"
                : "bg-white text-bark border-warm-border hover:border-bark/40"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-bark text-sm py-10 justify-center">
          <Loader2 size={16} className="animate-spin" /> Laddar flödet…
        </div>
      ) : items.length === 0 ? (
        <div data-testid="flode-empty" className="bg-bone-alt border border-warm-border rounded-sm p-6 text-sm text-bark">
          Inget i flödet ännu.
        </div>
      ) : (
        <ul className="flex flex-col gap-3" data-testid="flode-list">
          {items.map((it, idx) => {
            if (it.type === "signal") {
              const b = BADGES.signal;
              const BI = b.icon;
              return (
                <li
                  key={it.fingerprint || idx}
                  data-testid={`flode-item-signal-${it.fingerprint || idx}`}
                  className="bg-white border border-warm-border rounded-sm p-4"
                >
                  <div className="flex items-center gap-3 mb-2">
                    <Avatar name={it.source_hint} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-ink truncate">{it.source_hint || "Okänd källa"}</p>
                      <p className="text-xs text-bark/60">{relTime(it.ts)}</p>
                    </div>
                    <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-full border ${b.cls}`}>
                      <BI size={11} /> {SIGNAL_KIND_LABELS[it.kind] || it.kind}
                    </span>
                  </div>
                  {it.preview && <p className="text-sm text-ink/80 leading-relaxed line-clamp-3 mb-2">{it.preview}</p>}
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-bark">
                    {!it.reviewed && (
                      <Link to="/mod/event-sources" className="inline-flex items-center gap-1 text-falu font-semibold no-underline hover:underline">
                        <Eye size={12} /> Öppna granskningskön
                      </Link>
                    )}
                    {it.seen_count > 1 && <span>Sett {it.seen_count} gånger</span>}
                  </div>
                </li>
              );
            }
            const b = BADGES[it.badge] || BADGES.manuell;
            const BI = b.icon;
            return (
              <li
                key={it.id || idx}
                data-testid={`flode-item-evenemang-${it.id}`}
                className="bg-parchment border border-warm-border rounded-sm p-4"
              >
                <div className="flex items-center gap-3 mb-2">
                  <Avatar name={it.arrangor || it.source_name} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-ink truncate">
                      {it.arrangor || it.source_name || "Bygden"}
                      {it.autor ? <span className="font-normal text-bark/70"> · {it.autor}</span> : null}
                    </p>
                    <p className="text-xs text-bark/60">{relTime(it.ts)}</p>
                  </div>
                  <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-full border ${b.cls}`}>
                    <BI size={11} /> {b.label}
                  </span>
                </div>
                <h3 className={`font-heading text-base mb-1.5 ${it.badge === "avbokat" ? "text-bark/60 line-through" : "text-ink"}`}>
                  {it.titel}
                </h3>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-bark mb-2">
                  {it.datum && <span className="inline-flex items-center gap-1"><Calendar size={12} /> {it.datum}{it.datum_slut && it.datum_slut !== it.datum ? ` – ${it.datum_slut}` : ""}</span>}
                  {it.tid && <span className="inline-flex items-center gap-1"><Clock size={12} /> {it.tid}</span>}
                  {it.plats && <span className="inline-flex items-center gap-1"><MapPin size={12} /> {it.plats}</span>}
                </div>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
                  <Link to={`/evenemang/${it.id}`} className="inline-flex items-center gap-1 text-pine font-semibold no-underline hover:underline">
                    <Eye size={12} /> Öppna publikt
                  </Link>
                  {it.source_url && (
                    <a href={it.source_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-bark/70 no-underline hover:underline">
                      <ExternalLink size={12} /> Källa{it.source_name ? `: ${it.source_name}` : ""}
                    </a>
                  )}
                  {typeof it.confidence === "number" && (
                    <span className="text-bark/50">konfidens {Math.round(it.confidence * 100)} %</span>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
